import { motion, useTransform, useScroll } from "framer-motion";
import { useRef } from "react";
import logo from "@/assets/kr-logo.png";
import artistsPhoto from "@/assets/artists.png";
import { useIsMobile } from "@/hooks/use-mobile";
import { LiquidGlass } from "../animations/LiquidGlass";
import GalaxyBackground from "../animations/GalaxyBackground";

const Hero = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Track scroll for fade effects (disabled on mobile)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  
  const contentOpacity = useTransform(scrollYProgress, [0, 0.3, 0.6], isMobile ? [1, 1, 1] : [1, 0.5, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.6], isMobile ? [0, 0] : [0, -30]);

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden flex items-center justify-center">
      {/* 3D Galaxy Background with OrbitControls */}
      <GalaxyBackground variant="orangeWhite" transparent={false} />

      {/* Artists Photo - Right Bottom Corner */}
      <motion.div 
        className="absolute bottom-0 right-0 pointer-events-none"
        initial={isMobile ? { opacity: 1 } : { opacity: 0, x: 50, y: 50 }}
        animate={isMobile ? { opacity: 1 } : { opacity: 1, x: 0, y: 0 }}
        transition={isMobile ? {} : { duration: 2.5, ease: "easeOut" }}
        style={{ zIndex: 10 }}
      >
        {/* Rim light glow behind artists */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 50% 80% at 70% 50%, hsla(186, 60%, 63%, 0.15), hsla(315, 40%, 65%, 0.12) 40%, transparent 70%)",
            filter: "blur(80px)",
            zIndex: -1,
            transform: "scale(1.2)",
          }}
        />

        {/* NOTE: Image should be optimized to ~1600x2400px (2x retina) for h-[75vh] md:h-[85vh] display */}
        <motion.img
          src={artistsPhoto}
          alt="Klau y Ros Dance Artists"
          loading="eager"
          className="h-[75vh] md:h-[85vh] w-auto object-contain object-bottom"
          width={1600}
          height={2400}
          initial={isMobile ? {} : {
            filter: "grayscale(100%) brightness(0.2) saturate(0)",
          }}
          animate={isMobile ? {} : {
            filter: "grayscale(0%) brightness(0.95) saturate(1.05) contrast(1.02)",
          }}
          transition={isMobile ? {} : {
            duration: 2,
            delay: 2,
            ease: "easeInOut",
          }}
          style={{
            opacity: 0.7,
            mixBlendMode: "screen",
            maskImage:
              "linear-gradient(to left, rgba(0,0,0,1) 20%, rgba(0,0,0,0.9) 40%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.3) 80%, transparent 100%), linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.95) 80%, rgba(0,0,0,0.8) 92%, rgba(0,0,0,0.4) 97%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to left, rgba(0,0,0,1) 20%, rgba(0,0,0,0.9) 40%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.3) 80%, transparent 100%), linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.95) 80%, rgba(0,0,0,0.8) 92%, rgba(0,0,0,0.4) 97%, transparent 100%)",
            maskComposite: "intersect",
            WebkitMaskComposite: "source-in",
            filter: "drop-shadow(0 0 30px rgba(112, 210, 220, 0.1)) drop-shadow(0 0 50px rgba(207, 128, 187, 0.08))",
          }}
        />
      </motion.div>

      {/* Logo - Top Left */}
      <motion.div
        initial={isMobile ? { opacity: 1 } : { opacity: 0, x: -20 }}
        animate={isMobile ? { opacity: 1 } : { 
          opacity: 1, 
          x: 0,
          rotateY: [0, 360],
        }}
        transition={isMobile ? {} : { 
          opacity: { duration: 1, ease: "easeOut" },
          x: { duration: 1, ease: "easeOut" },
          rotateY: {
            duration: 7,
            repeat: Infinity,
            ease: "linear",
          },
        }}
        className="absolute top-8 left-8 pointer-events-none"
        style={{ perspective: isMobile ? 'none' : 1000, zIndex: 30 }}
      >
        {/* NOTE: Logo should be optimized to ~256x160px (2x retina: 512x320px) for this h-16 md:h-20 display */}
        <img
          src={logo}
          alt="Klau y Ros"
          loading="eager"
          className="h-16 md:h-20 w-auto drop-shadow-glow"
          width={256}
          height={160}
          style={{ transformStyle: "preserve-3d" }}
        />
      </motion.div>

      {/* Main Content */}
      <motion.div 
        className="relative text-center px-4 max-w-5xl mx-auto pointer-events-none"
        style={{ opacity: contentOpacity, y: contentY, zIndex: 30 }}
      >
        {/* CTA Buttons with Liquid Glass Effect */}
        <motion.div
          initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 20 }}
          animate={isMobile ? { opacity: 1 } : { opacity: 1, y: 0 }}
          transition={isMobile ? {} : { duration: 1, delay: 0.9, ease: "easeOut" }}
          style={{
            marginTop: "15rem",
            transform: "translateX(-8%)", // Shift slightly left to center under the "y" in "Klau y Ros"
          }}
          className="flex flex-col gap-4 justify-center items-center pointer-events-auto"
        >
          <LiquidGlass 
            wrapperClassName="hero-glass-cta"
            className="hero-glass-content"
          >
            <a 
              href="https://klauyros.artoverse.club/" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="elegant-cta-button elegant-cta-capsule elegant-cta-small">
                <span className="elegant-cta-text">Enter the Studio →</span>
              </button>
            </a>
          </LiquidGlass>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator - Simplified on mobile */}
      {!isMobile && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 pointer-events-none"
          style={{ zIndex: 30 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-6 h-10 border-2 border-primary/50 rounded-full flex items-start justify-center p-2"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-2 bg-primary rounded-full"
            />
          </motion.div>
        </motion.div>
      )}

      {/* Mobile Bottom Overlay - Smooth transition to About section */}
      <div 
        className="md:hidden absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 100%)",
          zIndex: 6,
        }}
      />
    </section>
  );
};

export default Hero;
